import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";

import type { PracticeBreakdown, PracticeMetrics } from "./types";

interface PracticeBreakdownTableProps {
  practiceBreakdown: PracticeBreakdown[];
  color: string;
  description: string[];
}

const getColumnHeader = (description: string[]) => {
  const descText = description.join(" ");
  const cleaned = descText.replace(/^\d+\s*/, "");
  return cleaned || "Posted";
};

const getMetricCount = (metrics: PracticeMetrics): number =>
  metrics.payment_count ??
  metrics.claims_count ??
  ((metrics as Record<string, unknown>).auto_matched_count as
    | number
    | undefined) ??
  ((metrics as Record<string, unknown>).autoMatchedCount as
    | number
    | undefined) ??
  ((metrics as Record<string, unknown>).count as number | undefined) ??
  0;

const getReviewCount = (metrics: PracticeMetrics) => metrics.review_count ?? 0;

const formatReviewText = (count: number) => {
  if (count === 1) {
    return `${count} Needs review`;
  }
  return `${count} Need review`;
};

const getAmountValue = (metrics: PracticeMetrics) =>
  metrics.total_amount ??
  metrics.total_payment_amount ??
  metrics.total_matched_amount ??
  null;

const formatAmount = (amount: number) =>
  `$${Math.round(amount).toLocaleString()}`;

const hasAmountData = (practiceBreakdown: PracticeBreakdown[]) =>
  practiceBreakdown.some(
    (practice) => getAmountValue(practice.metrics) !== null,
  );

export default function PracticeBreakdownTable({
  practiceBreakdown,
  color,
  description,
}: PracticeBreakdownTableProps) {
  const [zeroExpanded, setZeroExpanded] = useState(false);
  const showAmountColumn = hasAmountData(practiceBreakdown);

  const withValues = practiceBreakdown
    .filter((p) => getMetricCount(p.metrics) > 0)
    .sort(
      (a, b) =>
        Number(getMetricCount(b.metrics)) - Number(getMetricCount(a.metrics)),
    );
  const withoutValues = practiceBreakdown.filter(
    (p) => getMetricCount(p.metrics) === 0,
  );
  const visibleRows = zeroExpanded
    ? [...withValues, ...withoutValues]
    : withValues;

  return (
    <Box
      sx={{
        backgroundColor: "#F9FAFB",
        borderRadius: "8px",
        overflow: "hidden",
        border: "1px solid rgba(208, 213, 221, 0.5)",
        boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
        mt: 2,
      }}
    >
      <Table
        sx={{
          "& .MuiTableCell-root": {
            borderBottom: "none",
            fontFamily: "Inter Regular",
            outline: "none",
            "&:focus": {
              outline: "none",
            },
          },
          "& .MuiTypography-root": {
            outline: "none",
            "&:focus": {
              outline: "none",
            },
          },
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: color,
            }}
          >
            {/* Practice column header */}
            <TableCell
              sx={{
                padding: "16px 20px",
                width: "50%",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter Bold",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#475467",
                }}
              >
                Practice
              </Typography>
            </TableCell>

            {/* Claims Posted column header */}
            <TableCell
              align="center"
              sx={{
                padding: "16px 20px",
                width: "30%",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter Bold",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#475467",
                }}
              >
                {getColumnHeader(description)}
              </Typography>
            </TableCell>

            {/* Amount or Reviews column header */}
            <TableCell
              align="right"
              sx={{
                padding: "16px 20px",
                width: "20%",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter Bold",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#475467",
                }}
              >
                {showAmountColumn ? "Amount" : "Reviews"}
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map((practice) => (
            <TableRow
              key={practice.practiceId}
              sx={{
                backgroundColor: "#FFFFFF",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                },
              }}
            >
              <TableCell
                sx={{
                  padding: "16px 20px",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter Regular",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#101828",
                  }}
                >
                  {practice.practiceName}
                </Typography>
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  padding: "16px 20px",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter Bold",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#101828",
                  }}
                >
                  {String(getMetricCount(practice.metrics))}
                </Typography>
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  padding: "16px 20px",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter Regular",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#475467",
                  }}
                >
                  {showAmountColumn
                    ? formatAmount(getAmountValue(practice.metrics) ?? 0)
                    : formatReviewText(getReviewCount(practice.metrics))}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
          {withoutValues.length > 0 && (
            <TableRow
              onClick={() => setZeroExpanded((prev) => !prev)}
              sx={{
                backgroundColor: "#FFFFFF",
                cursor: "pointer",
                "&:hover": { backgroundColor: "#F3F4F6" },
              }}
            >
              <TableCell colSpan={3} sx={{ padding: "12px 20px" }}>
                <Typography
                  sx={{
                    fontFamily: "Inter Regular",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#667085",
                  }}
                >
                  {zeroExpanded
                    ? `Hide ${withoutValues.length} ${withoutValues.length === 1 ? "practice" : "practices"} with no ${getColumnHeader(description).toLowerCase()}`
                    : `+ ${withoutValues.length} ${withoutValues.length === 1 ? "practice" : "practices"} with no ${getColumnHeader(description).toLowerCase()}`}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
